import { getSelectedQueryModal } from "redux/SingleDataset/selectors";
import {
  setSelectedQueryModal,
  updateSelectedQueryModal,
} from "redux/SingleDataset/actions";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  EXACT_MATCH_QUERY_OPERATORS,
  ISSUES_SUB_TYPES,
  QueryOperator,
  FilterSubType,
  ExactMatchQueryOperators,
  VqlQuery,
  Filters,
} from "types";

import QueryModalOperators from "views/components/QueryModal/QueryModalOperators";
import QueryModalTitle from "views/components/QueryModal/QueryModalTitle";
import RangeInput from "views/components/QueryModal/RangeInput";
import QueryModal from "views/components/QueryModal";

import QualityIssuesIcon from "assets/icons/QualityIssuesIcon";
import styles from "./style.module.scss";
import {
  createAndUpdateIssueTypesFilter,
  isVqlQuery,
} from "helpers/utility/vqlHelpers";
import useVqlParams from "hooks/useVqlParams";
import {
  convertApiIssueOperator,
  convertAPIOperator,
} from "helpers/utility/queryFilterHelpers";
import { useIssuesData, IssueItem } from "./useIssuesData";

interface IssuesQueryModalProps {
  editQueryId?: string;
}

export default function IssuesQueryModal({
  editQueryId,
}: IssuesQueryModalProps) {
  const {
    updateVqlQuery,
    removeVqlQuery,
    findVqlQuery,
    addVqlQuery,
    vqlQueries,
  } = useVqlParams();

  const { issues } = useIssuesData();

  const oldQuery = editQueryId ? findVqlQuery(editQueryId, "issues") : null;
  const issueTextSet = new Set(
    issues.map((issue: IssueItem) => issue.text.toUpperCase()),
  );

  const selectedModalData = useSelector((state) =>
    getSelectedQueryModal(state),
  );
  const dispatch = useDispatch();

  const subIssues = ISSUES_SUB_TYPES.filter((subIssue) =>
    issueTextSet.has(subIssue),
  );

  const subIssuesWithDefault = [
    { value: null, label: "Choose Type" },
    ...subIssues,
  ];

  const queryOperator = oldQuery
    ? convertAPIOperator(oldQuery.issues.mode)
    : QueryOperator.IS;

  const querySubIssue = oldQuery
    ? convertApiIssueOperator(oldQuery.issues.value)
    : null;

  const range: [number, number] = oldQuery
    ? [oldQuery.issues.confidence_min, oldQuery.issues.confidence_max]
    : [0.8, 1];

  const [selectedOp, setSelectedOp] = useState<QueryOperator>(queryOperator);
  const [selectedSubIssue, setSelectedSubIssue] =
    useState<FilterSubType | null>(querySubIssue);

  const modalRef = useRef<HTMLDivElement>(null);

  const getDyncamicInfo = () => {
    switch (selectedSubIssue) {
      case FilterSubType.BLUR:
        return "Flags images that appear out of focus or lack sharp detail compared to the rest of the dataset.";
      case FilterSubType.BRIGHT:
        return "Detects images that are unusually bright or overexposed relative to the overall dataset.";
      case FilterSubType.DARK:
        return "Detects images that are unusually dark or underexposed relative to the overall dataset.";
      default:
        return undefined;
    }
  };

  const existingQuery = useMemo(() => {
    if (editQueryId) {
      return null;
    }
    return (
      vqlQueries.find(
        (query: VqlQuery) =>
          isVqlQuery(query, "issues") &&
          convertAPIOperator(query.issues?.mode) === selectedOp &&
          convertApiIssueOperator(query.issues?.value) === selectedSubIssue,
      ) || null
    );
  }, [vqlQueries, selectedOp, editQueryId, selectedSubIssue]);

  const submitQuery = (operator: QueryOperator, newRange: [number, number]) => {
    if (!selectedSubIssue) return;
    const queryId = editQueryId || existingQuery?.id;
    const vqlQuery = createAndUpdateIssueTypesFilter(
      selectedSubIssue,
      operator as ExactMatchQueryOperators,
      newRange,
      queryId,
    );
    queryId ? updateVqlQuery(vqlQuery) : addVqlQuery(vqlQuery);
    if (selectedModalData) {
      dispatch(
        setSelectedQueryModal({ ...selectedModalData, id: vqlQuery.id }),
      );
    }
  };

  const handleOptionSelection = (operator: QueryOperator) => {
    if (operator === selectedOp) return;
    setSelectedOp(operator);
    if (!selectedSubIssue) return;
    submitQuery(operator, range);
  };

  const handleRemoveIssue = () => {
    if (existingQuery) {
      removeVqlQuery(existingQuery.id);
      setSelectedSubIssue(null);
      if (vqlQueries.length === 1 && editQueryId) {
        dispatch(setSelectedQueryModal(null));
      }
    }
  };

  const handleSubIssueSelection = (value: FilterSubType | null) => {
    if (value === selectedSubIssue) return;
    if (existingQuery && !value) {
      handleRemoveIssue();
      return;
    }
    setSelectedSubIssue(value);
    submitQuery(selectedOp, [range[0], range[1]]);
  };

  const handleSubmission = (value: number[]) => {
    if (!selectedSubIssue) return;
    if (value[0] === range[0] && value[1] === range[1]) return;

    submitQuery(selectedOp, [value[0], value[1]]);
  };

  useEffect(() => {
    if (!selectedSubIssue) return;

    if (existingQuery) {
      dispatch(
        updateSelectedQueryModal({
          type: Filters.ISSUES,
          id: existingQuery.id,
        }),
      );
    } else {
      submitQuery(selectedOp, range);
      dispatch(updateSelectedQueryModal({ type: Filters.ISSUES }));
    }
    // eslint-disable-next-line
  }, [selectedOp, selectedSubIssue]);

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Delete" || event.key === "Backspace") {
      // Stop the event immediately
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      // Remove the query if it exists
      handleRemoveIssue();
    }
  };

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
    };
    //eslint-disable-next-line
  }, [selectedModalData, selectedSubIssue, selectedOp]);

  return (
    <QueryModal modalRef={modalRef} keepOpenOnLoading>
      <QueryModalTitle icon={<QualityIssuesIcon />} info={getDyncamicInfo()}>
        Quality Issue
      </QueryModalTitle>
      <div className={styles.filterContent}>
        <QueryModalOperators<QueryOperator>
          operators={[...EXACT_MATCH_QUERY_OPERATORS]}
          selectedOperator={selectedOp}
          onSelect={handleOptionSelection}
        />
        <QueryModalOperators<FilterSubType | null>
          operators={subIssuesWithDefault}
          selectedOperator={selectedSubIssue}
          onSelect={handleSubIssueSelection}
        />

        {selectedSubIssue && (
          <RangeInput
            range={range}
            title="Confidence:"
            onChange={handleSubmission}
          />
        )}
      </div>
    </QueryModal>
  );
}
